import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { getSortOptionFromURI } from "components/Filters/helpers";
import { ApiKey, SearchQueryOrderDirection } from "types/generated";

export type PersonalApiKeysSortField = keyof Pick<
  ApiKey,
  "name" | "id" | "owner" | "createdAt" | "expiresAt" | "lastUsedAt"
>;

type PersonalApiKeysSortableItem = Partial<Pick<ApiKey, PersonalApiKeysSortField>>;

type PersonalApiKeysInitialSort = {
  field: PersonalApiKeysSortField;
  direction: SearchQueryOrderDirection;
};

const getPersonalApiKeysSortValue = (
  apiKey: PersonalApiKeysSortableItem,
  field: PersonalApiKeysSortField
): string | number => {
  if (field === "expiresAt") {
    return apiKey.expiresAt ?? Infinity;
  }

  if (field === "lastUsedAt") {
    return apiKey.lastUsedAt ?? -Infinity;
  }

  return apiKey[field] ?? "";
};

const comparePersonalApiKeysSortValues = (valueA: string | number, valueB: string | number) => {
  if (typeof valueA === "number" && typeof valueB === "number") {
    return Math.sign(valueA - valueB) || 0;
  }

  return String(valueA).localeCompare(String(valueB), undefined, { numeric: true });
};

const usePersonalApiKeysTableSort = <T extends PersonalApiKeysSortableItem>(
  apiKeys: T[],
  sortFields: PersonalApiKeysSortField[],
  initialSort: PersonalApiKeysInitialSort
) => {
  const [searchParams] = useSearchParams();

  const orderBy = useMemo(
    () =>
      getSortOptionFromURI(searchParams, initialSort.field, initialSort.direction)[0] ??
      initialSort,
    [searchParams, initialSort]
  );

  return useMemo(() => {
    const sortField = sortFields.find((field) => field === orderBy.field);

    if (!sortField) {
      return apiKeys;
    }

    const direction = orderBy.direction === SearchQueryOrderDirection.Asc ? 1 : -1;

    return [...apiKeys].sort(
      (a, b) =>
        direction *
        comparePersonalApiKeysSortValues(
          getPersonalApiKeysSortValue(a, sortField),
          getPersonalApiKeysSortValue(b, sortField)
        )
    );
  }, [apiKeys, orderBy, sortFields]);
};

export default usePersonalApiKeysTableSort;
